
export enum Rol {
  SUPERADMIN = 'SuperAdmin',
  ADMIN = 'Admin',
  CAJERO = 'Cajero',
  VIEWER = 'Viewer',
  SOCIO = 'Socio',
}

export type Plan = string;
export type SuscripcionEstado = 'trialing' | 'active' | 'past_due' | 'canceled';

export interface PlanSuscripcion {
  id: number;
  activo: boolean;
  nombre: string;
  descripcion: string | null;
  precio: number;
  intervalo: 'mes' | 'año';
  limite_socios: number; // -1 represents unlimited
  mercadopago_plan_id: string | null;
  creado_en: string;
}

export interface Usuario {
  id: string; // Supabase auth user id is a UUID
  nombre: string;
  rol: Rol;
  email?: string;
  club_id?: number; // SuperAdmin might not have a club_id
  club_nombre?: string; // SuperAdmin might not have a club_nombre
  avatar_url?: string;
}

export interface Club {
  id: number;
  nombre: string;
  creado_en: string;
  plan: Plan;
  estado: 'activo' | 'inactivo' | 'suspendido';
  suscripcion_id?: string;
  suscripcion_estado: SuscripcionEstado;
  periodo_actual_fin?: string;
  administrador_id?: string;
  dominio?: string;
  subdominio?: string;
  modulos?: string[];
  logo_url?: string;
}

export interface ClubConteoSocios extends Club {
  socios_conteo: number;
  admin_nombre?: string;
  admin_email?: string;
}


export interface Socio {
  id: number;
  nombre: string;
  apellido: string;
  documento: string | null;
  telefono: string;
  email: string | null;
  notas: string;
  estado: 'activo' | 'inactivo';
  puntos: number;
  creado_por: string;
  creado_en: string;
  club_id: number;
  instagram?: string | null;
}

export interface Producto {
  id: number;
  nombre: string;
  categoria: string;
  precio: number;
  estado: 'activo' | 'inactivo';
  canjeable_por_puntos: boolean;
  puntos_para_canje?: number;
  club_id: number;
}

export enum MedioDePago {
  EFECTIVO = 'Efectivo',
  TRANSFERENCIA = 'Transferencia Bancaria',
}

export interface ItemVenta {
  id?: number;
  venta_id?: number;
  producto: Producto;
  producto_id: number;
  cantidad: number;
  precio_unitario: number;
}

export interface Venta {
  id: number;
  socio?: Socio;
  socio_id?: number | string | null;
  turno_id?: number | null; // turno que originó la venta (alquiler de cancha)
  items: ItemVenta[];
  total: number;
  medio_de_pago: MedioDePago;
  puntos_ganados: number;
  puntos_canjeados: number;
  socios_puntos_ids?: (string | number)[]; // Solo para distribución de puntos en frontend
  creado_por: string;
  creado_en: string;
  club_id: number;
}

export interface SesionCaja {
  id: number;
  abierta_por: string;
  monto_inicial: number;
  abierta_en: string;
  cerrada_por?: string;
  monto_final?: number;
  cerrada_en?: string;
  ventas: Venta[];
  estado: 'abierta' | 'cerrada';
  club_id: number;
}

export interface ReglaPuntos {
  id: number;
  descripcion: string;
  puntos: number;
  cada: number;
  categoria?: string;
  club_id: number;
}

export interface MovimientoPunto {
  id: number;
  socio_id: number;
  tipo: 'ganados' | 'canje' | 'ajuste';
  puntos: number;
  descripcion: string;
  creado_por: string;
  creado_en: string;
  club_id: number;
}

export interface Deuda {
  id: number;
  club_id: number;
  socio_id: string | number;
  socio?: Socio; // populado en fetch
  monto: number;
  descripcion: string | null;
  turno_id: number | null;
  venta_id: number | null;
  estado: 'pendiente' | 'pagada' | 'cancelada';
  creado_por: string;
  creado_en: string;
  pagada_por?: string | null;
  pagada_en?: string | null;
  cancelada_por?: string | null;
  cancelada_en?: string | null;
}

// Input para crear deudas desde el cobro de un turno.
export interface DeudaInput {
  socio_id: string | number;
  monto: number;
  descripcion?: string;
  turno_id?: number | null;
}

export interface AuditEvento {
  id: number;
  creado_en: string;
  tipo_evento: string;
  descripcion: string;
  metadata?: Record<string, any>;
  club_id?: number;
  suscripcion_id?: string;
}

// --- TIPOS NUEVOS PARA TURNOS ---

export type Deporte = 'Padel' | 'Futbol' | 'Tenis' | 'Hockey' | 'Otro';

export interface Cancha {
  id: number;
  nombre: string;
  deporte: Deporte;
  precio_base: number;
  duracion_minutos: number;
  estado: 'activa' | 'mantenimiento';
  club_id: number;
}

export interface TurnoParticipante {
  id?: number;
  turno_id?: number;
  socio_id?: number | string | null;
  nombre_libre?: string | null;
  socio?: Socio; // Para popular datos
}

// Input para crear/actualizar participantes de un turno.
// Si socio_id está seteado, es un socio registrado.
// Si nombre_libre está seteado, es un jugador por nombre (no socio).
export interface ParticipanteInput {
  socio_id?: string | number | null;
  nombre_libre?: string | null;
}

export interface Turno {
  id: number;
  cancha_id: number;
  cancha?: Cancha; // Para popular
  fecha_inicio: string; // ISO string
  fecha_fin: string; // ISO string
  precio: number;
  estado: 'reservado' | 'pagado' | 'cancelado' | 'jugando' | 'finalizado';
  socio_titular_id: number; // Quien reserva
  socio_titular?: Socio;
  notas: string;
  participantes?: TurnoParticipante[];
  creado_por: string;
  creado_en: string;
  club_id: number;
}
